import type { DrizzleD1Database } from 'drizzle-orm/d1';
import type * as schema from '../db/schema';
import type { Sandbox } from '@cloudflare/sandbox';


// Cloudflare Worker environment bindings
export interface Env {
    // D1 Database
    DB: D1Database;

    // R2 Object Storage
    R2: R2Bucket;

    // Workers AI
    AI: Ai;

    // Durable Objects
    EDITOR_SYNC: DurableObjectNamespace;

    // Workers for Platforms
    DISPATCHER: DispatchNamespace;

    // Sandbox (experimental)
    Sandbox: DurableObjectNamespace<Sandbox>;

    // Static Assets
    ASSETS: Fetcher;

    // Environment
    ENVIRONMENT?: string;
}


// Drizzle database type with schema
export type Database = DrizzleD1Database<typeof schema>;

// AI Model configuration
export interface AIModel {
    id: string;
    name: string;
    provider: string;
    description: string;
    contextWindow: number;
    isDefault?: boolean;
}

export const AVAILABLE_MODELS: AIModel[] = [
    {
        id: '@cf/qwen/qwen2.5-coder-32b-instruct',
        name: 'Qwen 2.5 Coder 32B',
        provider: 'Alibaba',
        description: 'Elite code generation with massive reasoning capabilities.',
        contextWindow: 128000,
        isDefault: true,
    },
    {
        id: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
        name: 'Llama 3.3 70B',
        provider: 'Meta',
        description: 'Blazing fast, high-quality code generation with massive context support.',
        contextWindow: 128000,
    },
    {
        id: '@cf/meta/llama-3.1-8b-instruct-fast',
        name: 'Llama 3.1 8B',
        provider: 'Meta',
        description: 'Lightweight and fast for simpler tasks',
        contextWindow: 128000,
    },
    {
        id: '@cf/deepseek-ai/deepseek-coder-6.7b-instruct-awq',
        name: 'DeepSeek Coder 6.7B',
        provider: 'DeepSeek',
        description: 'Specialized for code generation tasks',
        contextWindow: 16000,
    },
];

// Project file structure for generated apps
export interface ProjectFile {
    path: string;
    content: string;
    type: 'file' | 'directory';
}

// Generation request/response types
export interface GenerateRequest {
    prompt: string;
    projectId?: string;
    modelId?: string;
    context?: string[];
}

export interface GenerateResponse {
    projectId: string;
    files: ProjectFile[];
    status: 'success' | 'error';
    error?: string;
}

// WebSocket message types for real-time sync
export type WSMessage =
    | { type: 'file-update'; path: string; content: string }
    | { type: 'file-create'; path: string; content: string }
    | { type: 'file-delete'; path: string }
    | { type: 'preview-refresh' }
    | { type: 'generation-start'; prompt: string }
    | { type: 'generation-progress'; chunk: string }
    | { type: 'generation-complete'; files: ProjectFile[] }
    | { type: 'generation-error'; error: string }
    | { type: 'deploy-error'; error: string }
    | { type: 'terminal-output'; content: string; stream: 'stdout' | 'stderr' }
    | { type: 'terminal-clear' };
